// src/monitoring/i18n.js
//
// Every string the Monitoring screens show, in English and Greek, keyed. The
// screens compose numbers into these at render time, which is why they do not
// rely on the DOM-level legacy bridge (it can only translate whole text nodes
// it has seen before). Terminology follows the reviewed PV vocabulary:
// inverter = αντιστροφέας, string = στοιχειοσειρά, module = πλαίσιο.

import { useCallback } from "react";
import { useLanguage } from "../context/LanguageContext";

const EN = {
  "nav.assetMonitoring": "Asset Monitoring",
  "shell.title": "Asset Monitoring",
  "shell.subtitle": "Live production, expected production and alarms for every operating PV park of the project.",
  "shell.refresh": "Refresh",
  "shell.noProject": "Save or open a project to monitor its parks.",
  "shell.signIn": "Sign in to load Monitoring.",
  "shell.simulated": "Simulated data",
  "shell.simulatedHint": "One or more parks run on the simulated source: telemetry is generated from the park's plant parameters and the sky over its location, not read from a portal.",
  "shell.replay": "Replayed data",
  "shell.replayHint": "One or more parks contain a validated CSV import or a time-shifted replay. This is not a live portal connection.",
  "shell.loading": "Loading monitoring…",

  "portfolio.title": "Portfolio",
  "portfolio.livePower": "Live power",
  "portfolio.expectedNow": "expected now",
  "portfolio.energyToday": "Energy today",
  "portfolio.ofExpected": "of expected",
  "portfolio.prToday": "Energy performance index today (EPI)",
  "portfolio.openAlarms": "Open alarms",
  "portfolio.inverters": "Inverters reporting",
  "portfolio.producing": "producing",
  "portfolio.awaitingVerification": "awaiting next poll",
  "portfolio.installed": "Installed",
  "portfolio.parks": "Parks",
  "portfolio.energy30": "Energy, last 30 days",
  "portfolio.actual": "Actual",
  "portfolio.expected": "Expected",
  "portfolio.empty": "No parks are monitored yet.",
  "portfolio.emptyHint": "Add a park and its data source under Data Sources, or load the demonstration portfolio to see the screens populated.",
  "portfolio.goSources": "Data Sources",
  "portfolio.loadDemo": "Load demonstration portfolio",
  "portfolio.map": "Map",
  "portfolio.col.park": "Park",
  "portfolio.col.status": "Status",
  "portfolio.col.power": "Power",
  "portfolio.col.expected": "Expected",
  "portfolio.col.today": "Today",
  "portfolio.col.pr": "EPI",
  "portfolio.col.inverters": "Inverters",
  "portfolio.col.alarms": "Alarms",
  "portfolio.col.lastReading": "Last reading",

  "status.ok": "Normal",
  "status.warning": "Warning",
  "status.critical": "Critical",
  "status.stale": "Stale data",
  "status.no_data": "No data",
  "status.not_wired": "Awaiting connector",
  "status.disabled": "Disabled",

  "severity.info": "Info",
  "severity.warning": "Warning",
  "severity.critical": "Critical",

  "park.back": "Portfolio",
  "park.powerToday": "Power today",
  "park.powerVsExpected": "Actual against expected power",
  "park.basis.weather": "expected from weather",
  "park.basis.clearsky": "expected from clear sky (weather feed unavailable)",
  "park.basis.project_forecast": "expected from the project's stored PV generation estimate",
  "park.basis.imported": "expected power imported from the validated CSV",
  "park.inverters": "Inverters",
  "park.invertersHint": "Coloured by today's specific yield against the park median. Grey: no data.",
  "park.specificYield": "specific yield",
  "park.median": "park median",
  "park.openAlarms": "Open alarms",
  "park.noOpenAlarms": "No open alarms.",
  "park.recentAlarms": "Alarm history, last 30 days",
  "park.daily": "Daily performance, last 30 days",
  "park.pr": "Energy performance index (EPI)",
  "park.availability": "Availability",
  "park.commsAvailability": "Data availability",
  "park.energy": "Energy",
  "park.peak": "Peak",
  "park.devices": "Other devices",
  "park.plantMeter": "Plant meter",
  "park.irradiance": "Irradiance",
  "park.temperature": "Temperature",
  "park.capacity": "Capacity",
  "park.commissioned": "Commissioned",
  "park.equipment": "Equipment",
  "park.day": "Day",
  "park.mppt": "MPPT",
  "park.status": "Status",
  "park.notFound": "This park is not in the current project.",
  "park.pendingVerification": "A resolved alarm is awaiting a new telemetry poll. The park remains degraded until the next poll verifies recovery.",
  "park.noSeries": "No samples for this day.",
  "park.kind.inverter": "Inverter",
  "park.kind.plant": "Plant meter",
  "park.kind.weather": "Weather sensor",
  "park.kind.meter": "Meter",
  "park.kind.string": "String",

  "alarms.title": "Alarms",
  "alarms.subtitle": "Every condition the rules currently see, one row per condition for as long as it lasts. Acknowledging marks that someone is on it; resolving closes the row - the next sweep re-opens it if the fault persists.",
  "alarms.filter.status": "Show",
  "alarms.filter.open": "Open",
  "alarms.filter.closed": "Closed",
  "alarms.filter.all": "All",
  "alarms.filter.park": "Park",
  "alarms.filter.allParks": "All parks",
  "alarms.col.severity": "Severity",
  "alarms.col.park": "Park",
  "alarms.col.device": "Device",
  "alarms.col.rule": "Rule",
  "alarms.col.message": "Condition",
  "alarms.col.opened": "Opened",
  "alarms.col.closed": "Closed",
  "alarms.col.actions": "",
  "alarms.ack": "Acknowledge",
  "alarms.acked": "Acknowledged",
  "alarms.resolve": "Resolve",
  "alarms.empty": "No alarms match.",
  "alarms.rules": "Alarm rules",
  "alarms.rulesHint": "Platform defaults, adjustable per project. Thresholds are meant to be tuned per park after two or three weeks of data.",
  "alarms.rule.enabled": "Enabled",
  "alarms.rule.severity": "Severity",
  "alarms.rule.params": "Parameters",
  "alarms.rules.save": "Save rules",
  "alarms.rules.saved": "Rules saved.",
  "alarms.notifications": "Notifications",
  "alarms.notificationsHint": "Who is told when an alarm opens. E-mail is delivered once the platform's mail server is configured; SMS and Viber recipients are stored for the gateway.",
  "alarms.notif.channel": "Channel",
  "alarms.notif.target": "Recipient",
  "alarms.notif.minSeverity": "From severity",
  "alarms.notif.add": "Add recipient",
  "alarms.notif.save": "Save recipients",
  "alarms.notif.saved": "Recipients saved.",
  "alarms.notif.mailNotConfigured": "E-mail delivery is not configured on this deployment yet; recipients are stored and will be served once it is.",
  "alarms.notif.mailConfigured": "E-mail delivery is active.",

  "rule.comm_loss": "Communication loss",
  "rule.inverter_offline": "Inverter offline",
  "rule.inverter_underperformance": "Inverter underperforming its peers",
  "rule.plant_underperformance": "Park below expected production",
  "rule.string_deviation": "String current deviation",
  "rule.vendor_event": "Fault reported by the portal",
  "rule.desc.comm_loss": "No sample from the inverter for {{missed_intervals}} polling intervals in daylight.",
  "rule.desc.inverter_offline": "Zero output for {{samples}} consecutive samples while the other inverters run above {{siblings_min_share_pct}}% of capacity.",
  "rule.desc.inverter_underperformance": "Below {{ratio_pct}}% of the park's median specific power for {{samples}} consecutive samples.",
  "rule.desc.plant_underperformance": "Today's energy below {{ratio_weather_pct}}% of expected (weather basis) or {{ratio_clearsky_pct}}% (clear-sky basis).",
  "rule.desc.string_deviation": "One MPPT current more than {{deviation_pct}}% below the inverter's others for {{samples}} samples.",
  "rule.desc.vendor_event": "An open fault or event in the vendor portal's feed.",

  "msg.comm_loss": "{{device}}: no data for {{minutes}} min",
  "msg.comm_loss.never": "{{device}}: no data received",
  "msg.inverter_offline": "{{device}}: no output while the park runs at {{siblings_pct}}% of capacity",
  "msg.inverter_underperformance": "{{device}}: producing {{relative_pct}}% of its peers",
  "msg.plant_underperformance": "Park at {{ratio_pct}}% of expected production today",
  "msg.plant_underperformance.clearsky": "Park at {{ratio_pct}}% of clear-sky production today (weather feed unavailable)",
  "msg.string_deviation": "{{device}}: MPPT {{mppts}} current below the others",
  "msg.vendor_event": "{{device}}{{code}}: {{vendor_message}}",

  "reports.title": "Monthly report",
  "reports.subtitle": "Energy, expected energy, energy performance index and availability per park for the month, from the daily roll-ups. Contractual PR remains a separate irradiance-based KPI.",
  "reports.month": "Month",
  "reports.col.park": "Park",
  "reports.col.capacity": "Capacity",
  "reports.col.energy": "Energy",
  "reports.col.expected": "Expected",
  "reports.col.pr": "EPI",
  "reports.col.prTarget": "EPI target",
  "reports.col.yield": "Specific yield",
  "reports.col.availability": "Availability",
  "reports.col.comms": "Data availability",
  "reports.col.peak": "Peak",
  "reports.col.alarms": "Alarms",
  "reports.col.days": "Days with data",
  "reports.total": "Portfolio",
  "reports.empty": "No daily data for this month.",
  "reports.dailyEnergy": "Daily energy per park",

  "sources.title": "Data Sources",
  "sources.subtitle": "Each monitored park: what it is (plant parameters) and where its data comes from (portal and credentials). The screens above read only what is stored here.",
  "sources.add": "Add park",
  "sources.demo": "Load demonstration portfolio",
  "sources.demoHint": "Creates five simulated Greek parks with two weeks of history and a few live faults. Everything it makes is marked simulated and can be deleted here.",
  "sources.demoRunning": "Building the demonstration portfolio…",
  "sources.demoDone": "Demonstration portfolio ready.",
  "sources.projectSim": "Simulate this project",
  "sources.projectSimTitle": "Simulate the active PV asset",
  "sources.projectSimHint": "Creates one monitored park from the saved project and its latest stored PV generation estimate. It generates operating history, actual-versus-expected production, and active and historical faults. Everything is marked as simulated.",
  "sources.projectSimDays": "Operating history (days)",
  "sources.projectSimDaysHint": "Choose from 7 to 365 days.",
  "sources.projectSimReplace": "Replace the generic five-park demonstration portfolio",
  "sources.projectSimForecast": "The project must be saved and the Estimate under PV Generation must be current.",
  "sources.projectSimRunning": "Building the project simulation…",
  "sources.projectSimCreate": "Create simulation",
  "sources.projectSimDone": "{{name}} is ready with {{days}} days of simulated operating history.",
  "sources.projectSimFailed": "The project simulation could not be created.",
  "sources.col.park": "Park",
  "sources.col.vendor": "Source",
  "sources.col.capacity": "Capacity",
  "sources.col.interval": "Polling",
  "sources.col.lastPoll": "Last poll",
  "sources.col.lastReading": "Last reading",
  "sources.col.status": "Status",
  "sources.pollNow": "Poll now",
  "sources.test": "Test connection",
  "sources.edit": "Edit",
  "sources.delete": "Delete",
  "sources.deleteConfirm": "Delete this park and all its readings and alarms?",
  "sources.enabled": "Enabled",
  "sources.disabled": "Disabled",
  "sources.poll.ok": "OK",
  "sources.poll.error": "Error",
  "sources.poll.not_wired": "Awaiting connector",
  "sources.poll.never": "Never polled",
  "sources.everyMin": "every {{n}} min",
  "sources.empty": "No parks yet. Add the first one, or load the demonstration portfolio.",
  "sources.wiredHint": "The API client for this portal is prepared but not connected yet: credentials are stored and polling starts once it is.",
  "sources.testOk": "Connection OK.",
  "sources.testFailed": "Connection failed",
  "sources.polled": "Polled: {{n}} samples, {{alarms}} active conditions.",
  "csv.open": "Import CSV / Replay",
  "csv.title": "Import monitoring CSV",
  "csv.title.append": "Import more CSV data",
  "csv.intro": "Validate a read-only portal or SCADA export, then preserve its timestamps as history or shift the complete series to the present for a controlled replay. PYTHIA stores normalized telemetry and a checksum; it does not retain the raw file.",
  "csv.template": "Download template",
  "csv.choose": "Choose CSV",
  "csv.noFile": "No file selected",
  "csv.mode": "Import mode",
  "csv.mode.replay": "Replay up to now",
  "csv.mode.historical": "Preserve historical time",
  "csv.mode.replayHint": "Only timestamps are shifted. Measurement values and spacing are not changed. The latest sample lands on the latest completed interval.",
  "csv.mode.historicalHint": "Original timestamps are retained. Historical imports do not create currently open alarms.",
  "csv.validate": "Validate",
  "csv.import": "Import",
  "csv.valid": "Validation passed",
  "csv.summary": "{{readings}} readings · {{devices}} devices · {{inverters}} inverters · {{events}} vendor events · {{sample_min}}-minute resolution",
  "csv.imported": "Imported {{rows}} readings from {{devices}} devices.",
  "csv.append": "Import another CSV",
  "csv.badge": "CSV / replay",
  "csv.sampleEvery": "{{n}} min samples",

  "form.title.new": "New park",
  "form.title.edit": "Edit park",
  "form.section.identity": "Park",
  "form.section.plant": "Plant parameters",
  "form.section.plantHint": "What the expected-production model and the alarm rules need. Capacity and location are required; the rest improves the expected curve.",
  "form.section.source": "Data source",
  "form.section.sourceHint": "The portal the park reports to today. Nothing is replaced on site.",
  "form.name": "Park name",
  "form.asset": "Platform asset (optional)",
  "form.assetHint": "Link to an asset of this project so the park appears in Operations too.",
  "form.assetNone": "Not linked",
  "form.dc": "Installed DC capacity (kWp)",
  "form.ac": "AC capacity (kW)",
  "form.lat": "Latitude",
  "form.lon": "Longitude",
  "form.tz": "Time zone",
  "form.tilt": "Tilt (°)",
  "form.azimuth": "Azimuth (°, 180 = south)",
  "form.mounting": "Mounting",
  "form.mounting.fixed": "Fixed tilt",
  "form.mounting.tracker": "Single-axis tracker",
  "form.commissioned": "Commissioning date",
  "form.module": "Module model",
  "form.inverterModel": "Inverter model",
  "form.inverterCount": "Number of inverters",
  "form.prTarget": "EPI target (0-1)",
  "form.prTargetHint": "The operational actual-to-expected energy target. This is not the contractual irradiance-based PR.",
  "form.loss": "System losses (%)",
  "form.lossHint": "Wiring, soiling, mismatch and availability together. Tune after a few weeks of data.",
  "form.vendor": "Portal / vendor",
  "form.vendorSite": "Plant identifier in the portal",
  "form.vendorSiteHint": "The station / site / system id the portal uses for this park. Leave empty to take the first plant the credentials can see.",
  "form.interval": "Polling interval (min)",
  "form.intervalHint": "At least {{min}} min for this portal.",
  "form.enabled": "Polling enabled",
  "form.credentialsKept": "Stored credentials are kept unless you enter new ones.",
  "form.accessHowto": "How the client grants access",
  "form.capabilities": "Delivers",
  "form.cap.inverterLevel": "inverter level",
  "form.cap.stringLevel": "string level",
  "form.cap.moduleLevel": "module level (with optimisers)",
  "form.cap.events": "fault events",
  "form.cap.siteMeter": "plant meter",
  "form.costNote": "Cost",
  "form.save": "Save park",
  "form.cancel": "Cancel",
  "form.required": "Required",
  "form.error": "Could not save the park.",

  "common.kw": "kW",
  "common.kwp": "kWp",
  "common.kwh": "kWh",
  "common.mwh": "MWh",
  "common.na": "—",
  "common.of": "of",
  "common.min": "min",
  "common.error": "Something went wrong.",
  "common.retry": "Retry",
};

const EL = {
  "nav.assetMonitoring": "Παρακολούθηση Μονάδων",
  "shell.title": "Παρακολούθηση Μονάδων",
  "shell.subtitle": "Παραγωγή σε πραγματικό χρόνο, αναμενόμενη παραγωγή και συναγερμοί για κάθε Φ/Β πάρκο του έργου σε λειτουργία.",
  "shell.refresh": "Ανανέωση",
  "shell.noProject": "Αποθηκεύστε ή ανοίξτε ένα έργο για να παρακολουθήσετε τα πάρκα του.",
  "shell.signIn": "Συνδεθείτε για να φορτωθεί η Παρακολούθηση.",
  "shell.simulated": "Προσομοιωμένα δεδομένα",
  "shell.simulatedHint": "Ένα ή περισσότερα πάρκα τρέχουν στην προσομοιωμένη πηγή: οι μετρήσεις παράγονται από τα χαρακτηριστικά του πάρκου και τον ουρανό πάνω από την τοποθεσία του, δεν διαβάζονται από portal.",
  "shell.replay": "Δεδομένα replay",
  "shell.replayHint": "Ένα ή περισσότερα πάρκα περιέχουν ελεγμένη εισαγωγή CSV ή χρονικά μετατοπισμένο replay. Δεν πρόκειται για ζωντανή σύνδεση portal.",
  "shell.loading": "Φόρτωση παρακολούθησης…",

  "portfolio.title": "Χαρτοφυλάκιο",
  "portfolio.livePower": "Τρέχουσα ισχύς",
  "portfolio.expectedNow": "αναμενόμενη τώρα",
  "portfolio.energyToday": "Ενέργεια σήμερα",
  "portfolio.ofExpected": "της αναμενόμενης",
  "portfolio.prToday": "Δείκτης ενεργειακής απόδοσης σήμερα (EPI)",
  "portfolio.openAlarms": "Ανοικτοί συναγερμοί",
  "portfolio.inverters": "Αντιστροφείς που αναφέρουν",
  "portfolio.producing": "σε παραγωγή",
  "portfolio.awaitingVerification": "αναμονή νέας λήψης",
  "portfolio.installed": "Εγκατεστημένη ισχύς",
  "portfolio.parks": "Πάρκα",
  "portfolio.energy30": "Ενέργεια, τελευταίες 30 ημέρες",
  "portfolio.actual": "Πραγματική",
  "portfolio.expected": "Αναμενόμενη",
  "portfolio.empty": "Δεν παρακολουθείται ακόμη κανένα πάρκο.",
  "portfolio.emptyHint": "Προσθέστε ένα πάρκο και την πηγή δεδομένων του στις Πηγές Δεδομένων, ή φορτώστε το χαρτοφυλάκιο επίδειξης για να δείτε τις οθόνες με δεδομένα.",
  "portfolio.goSources": "Πηγές Δεδομένων",
  "portfolio.loadDemo": "Φόρτωση χαρτοφυλακίου επίδειξης",
  "portfolio.map": "Χάρτης",
  "portfolio.col.park": "Πάρκο",
  "portfolio.col.status": "Κατάσταση",
  "portfolio.col.power": "Ισχύς",
  "portfolio.col.expected": "Αναμενόμενη",
  "portfolio.col.today": "Σήμερα",
  "portfolio.col.pr": "EPI",
  "portfolio.col.inverters": "Αντιστροφείς",
  "portfolio.col.alarms": "Συναγερμοί",
  "portfolio.col.lastReading": "Τελευταία μέτρηση",

  "status.ok": "Κανονική",
  "status.warning": "Προειδοποίηση",
  "status.critical": "Κρίσιμη",
  "status.stale": "Παλαιά δεδομένα",
  "status.no_data": "Χωρίς δεδομένα",
  "status.not_wired": "Αναμονή σύνδεσης",
  "status.disabled": "Ανενεργό",

  "severity.info": "Πληροφορία",
  "severity.warning": "Προειδοποίηση",
  "severity.critical": "Κρίσιμο",

  "park.back": "Χαρτοφυλάκιο",
  "park.powerToday": "Ισχύς σήμερα",
  "park.powerVsExpected": "Πραγματική έναντι αναμενόμενης ισχύος",
  "park.basis.weather": "αναμενόμενη από μετεωρολογικά δεδομένα",
  "park.basis.clearsky": "αναμενόμενη από καθαρό ουρανό (μη διαθέσιμη μετεωρολογική ροή)",
  "park.basis.project_forecast": "αναμενόμενη από την αποθηκευμένη εκτίμηση παραγωγής ΦΒ του έργου",
  "park.basis.imported": "αναμενόμενη ισχύς από το ελεγμένο αρχείο CSV",
  "park.inverters": "Αντιστροφείς",
  "park.invertersHint": "Χρωματισμός με βάση τη σημερινή ειδική απόδοση σε σχέση με τη διάμεσο του πάρκου. Γκρι: χωρίς δεδομένα.",
  "park.specificYield": "ειδική απόδοση",
  "park.median": "διάμεσος πάρκου",
  "park.openAlarms": "Ανοικτοί συναγερμοί",
  "park.noOpenAlarms": "Δεν υπάρχουν ανοικτοί συναγερμοί.",
  "park.recentAlarms": "Ιστορικό συναγερμών, τελευταίες 30 ημέρες",
  "park.daily": "Ημερήσια απόδοση, τελευταίες 30 ημέρες",
  "park.pr": "Δείκτης ενεργειακής απόδοσης (EPI)",
  "park.availability": "Διαθεσιμότητα",
  "park.commsAvailability": "Διαθεσιμότητα δεδομένων",
  "park.energy": "Ενέργεια",
  "park.peak": "Μέγιστη ισχύς",
  "park.devices": "Λοιπές συσκευές",
  "park.plantMeter": "Μετρητής πάρκου",
  "park.irradiance": "Ακτινοβολία",
  "park.temperature": "Θερμοκρασία",
  "park.capacity": "Ισχύς",
  "park.commissioned": "Ηλέκτριση",
  "park.equipment": "Εξοπλισμός",
  "park.day": "Ημέρα",
  "park.mppt": "MPPT",
  "park.status": "Κατάσταση",
  "park.notFound": "Το πάρκο δεν ανήκει στο τρέχον έργο.",
  "park.pendingVerification": "Ένας επιλυμένος συναγερμός αναμένει νέα λήψη τηλεμετρίας. Το πάρκο παραμένει σε υποβαθμισμένη κατάσταση μέχρι η επόμενη λήψη να επιβεβαιώσει την αποκατάσταση.",
  "park.noSeries": "Δεν υπάρχουν μετρήσεις για αυτή την ημέρα.",
  "park.kind.inverter": "Αντιστροφέας",
  "park.kind.plant": "Μετρητής πάρκου",
  "park.kind.weather": "Μετεωρολογικός αισθητήρας",
  "park.kind.meter": "Μετρητής",
  "park.kind.string": "Στοιχειοσειρά",

  "alarms.title": "Συναγερμοί",
  "alarms.subtitle": "Κάθε συνθήκη που εντοπίζουν οι κανόνες, μία γραμμή ανά συνθήκη για όσο διαρκεί. Η επιβεβαίωση δηλώνει ότι κάποιος την ανέλαβε· η επίλυση κλείνει τη γραμμή — ο επόμενος έλεγχος την ανοίγει ξανά αν το σφάλμα παραμένει.",
  "alarms.filter.status": "Εμφάνιση",
  "alarms.filter.open": "Ανοικτοί",
  "alarms.filter.closed": "Κλειστοί",
  "alarms.filter.all": "Όλοι",
  "alarms.filter.park": "Πάρκο",
  "alarms.filter.allParks": "Όλα τα πάρκα",
  "alarms.col.severity": "Σοβαρότητα",
  "alarms.col.park": "Πάρκο",
  "alarms.col.device": "Συσκευή",
  "alarms.col.rule": "Κανόνας",
  "alarms.col.message": "Συνθήκη",
  "alarms.col.opened": "Έναρξη",
  "alarms.col.closed": "Λήξη",
  "alarms.col.actions": "",
  "alarms.ack": "Επιβεβαίωση",
  "alarms.acked": "Επιβεβαιώθηκε",
  "alarms.resolve": "Επίλυση",
  "alarms.empty": "Δεν υπάρχουν συναγερμοί με αυτά τα κριτήρια.",
  "alarms.rules": "Κανόνες συναγερμών",
  "alarms.rulesHint": "Προεπιλογές της πλατφόρμας, ρυθμιζόμενες ανά έργο. Τα όρια προσαρμόζονται ανά πάρκο μετά από δύο-τρεις εβδομάδες δεδομένων.",
  "alarms.rule.enabled": "Ενεργός",
  "alarms.rule.severity": "Σοβαρότητα",
  "alarms.rule.params": "Παράμετροι",
  "alarms.rules.save": "Αποθήκευση κανόνων",
  "alarms.rules.saved": "Οι κανόνες αποθηκεύτηκαν.",
  "alarms.notifications": "Ειδοποιήσεις",
  "alarms.notificationsHint": "Ποιος ενημερώνεται όταν ανοίγει συναγερμός. Το e-mail αποστέλλεται μόλις ρυθμιστεί ο διακομιστής αλληλογραφίας της πλατφόρμας· οι παραλήπτες SMS και Viber αποθηκεύονται για την πύλη αποστολής.",
  "alarms.notif.channel": "Κανάλι",
  "alarms.notif.target": "Παραλήπτης",
  "alarms.notif.minSeverity": "Από σοβαρότητα",
  "alarms.notif.add": "Προσθήκη παραλήπτη",
  "alarms.notif.save": "Αποθήκευση παραληπτών",
  "alarms.notif.saved": "Οι παραλήπτες αποθηκεύτηκαν.",
  "alarms.notif.mailNotConfigured": "Η αποστολή e-mail δεν έχει ρυθμιστεί ακόμη σε αυτή την εγκατάσταση· οι παραλήπτες αποθηκεύονται και θα εξυπηρετηθούν μόλις ρυθμιστεί.",
  "alarms.notif.mailConfigured": "Η αποστολή e-mail είναι ενεργή.",

  "rule.comm_loss": "Απώλεια επικοινωνίας",
  "rule.inverter_offline": "Αντιστροφέας εκτός λειτουργίας",
  "rule.inverter_underperformance": "Αντιστροφέας με χαμηλότερη απόδοση από τους υπόλοιπους",
  "rule.plant_underperformance": "Πάρκο κάτω από την αναμενόμενη παραγωγή",
  "rule.string_deviation": "Απόκλιση ρεύματος στοιχειοσειράς",
  "rule.vendor_event": "Σφάλμα από το portal του κατασκευαστή",
  "rule.desc.comm_loss": "Καμία μέτρηση από τον αντιστροφέα για {{missed_intervals}} διαστήματα λήψης κατά τη διάρκεια της ημέρας.",
  "rule.desc.inverter_offline": "Μηδενική ισχύς για {{samples}} συνεχόμενες μετρήσεις ενώ οι υπόλοιποι αντιστροφείς λειτουργούν πάνω από το {{siblings_min_share_pct}}% της ισχύος τους.",
  "rule.desc.inverter_underperformance": "Κάτω από το {{ratio_pct}}% της διαμέσου ειδικής ισχύος του πάρκου για {{samples}} συνεχόμενες μετρήσεις.",
  "rule.desc.plant_underperformance": "Η σημερινή ενέργεια κάτω από το {{ratio_weather_pct}}% της αναμενόμενης (με μετεωρολογικά δεδομένα) ή το {{ratio_clearsky_pct}}% (με καθαρό ουρανό).",
  "rule.desc.string_deviation": "Ρεύμα ενός MPPT περισσότερο από {{deviation_pct}}% κάτω από τα υπόλοιπα του αντιστροφέα για {{samples}} μετρήσεις.",
  "rule.desc.vendor_event": "Ανοικτό σφάλμα ή συμβάν στη ροή του portal του κατασκευαστή.",

  "msg.comm_loss": "{{device}}: χωρίς δεδομένα για {{minutes}} λεπτά",
  "msg.comm_loss.never": "{{device}}: δεν έχουν ληφθεί δεδομένα",
  "msg.inverter_offline": "{{device}}: μηδενική ισχύς ενώ το πάρκο λειτουργεί στο {{siblings_pct}}% της ισχύος του",
  "msg.inverter_underperformance": "{{device}}: παράγει το {{relative_pct}}% των υπολοίπων",
  "msg.plant_underperformance": "Το πάρκο στο {{ratio_pct}}% της αναμενόμενης παραγωγής σήμερα",
  "msg.plant_underperformance.clearsky": "Το πάρκο στο {{ratio_pct}}% της παραγωγής καθαρού ουρανού σήμερα (μη διαθέσιμη μετεωρολογική ροή)",
  "msg.string_deviation": "{{device}}: ρεύμα MPPT {{mppts}} χαμηλότερο από τα υπόλοιπα",
  "msg.vendor_event": "{{device}}{{code}}: {{vendor_message}}",

  "reports.title": "Μηνιαία αναφορά",
  "reports.subtitle": "Ενέργεια, αναμενόμενη ενέργεια, δείκτης ενεργειακής απόδοσης και διαθεσιμότητα ανά πάρκο για τον μήνα, από τα ημερήσια σύνολα. Το συμβατικό PR παραμένει ξεχωριστός δείκτης που βασίζεται στην ακτινοβολία.",
  "reports.month": "Μήνας",
  "reports.col.park": "Πάρκο",
  "reports.col.capacity": "Ισχύς",
  "reports.col.energy": "Ενέργεια",
  "reports.col.expected": "Αναμενόμενη",
  "reports.col.pr": "EPI",
  "reports.col.prTarget": "Στόχος EPI",
  "reports.col.yield": "Ειδική απόδοση",
  "reports.col.availability": "Διαθεσιμότητα",
  "reports.col.comms": "Διαθεσιμότητα δεδομένων",
  "reports.col.peak": "Μέγιστη ισχύς",
  "reports.col.alarms": "Συναγερμοί",
  "reports.col.days": "Ημέρες με δεδομένα",
  "reports.total": "Χαρτοφυλάκιο",
  "reports.empty": "Δεν υπάρχουν ημερήσια δεδομένα για αυτόν τον μήνα.",
  "reports.dailyEnergy": "Ημερήσια ενέργεια ανά πάρκο",

  "sources.title": "Πηγές Δεδομένων",
  "sources.subtitle": "Κάθε παρακολουθούμενο πάρκο: τι είναι (χαρακτηριστικά εγκατάστασης) και από πού έρχονται τα δεδομένα του (portal και διαπιστευτήρια). Οι υπόλοιπες οθόνες διαβάζουν μόνο όσα αποθηκεύονται εδώ.",
  "sources.add": "Προσθήκη πάρκου",
  "sources.demo": "Φόρτωση χαρτοφυλακίου επίδειξης",
  "sources.demoHint": "Δημιουργεί πέντε προσομοιωμένα ελληνικά πάρκα με ιστορικό δύο εβδομάδων και μερικά ενεργά σφάλματα. Ό,τι δημιουργεί σημειώνεται ως προσομοιωμένο και διαγράφεται από εδώ.",
  "sources.demoRunning": "Δημιουργία χαρτοφυλακίου επίδειξης…",
  "sources.demoDone": "Το χαρτοφυλάκιο επίδειξης είναι έτοιμο.",
  "sources.projectSim": "Προσομοίωση αυτού του έργου",
  "sources.projectSimTitle": "Προσομοίωση της ενεργής Φ/Β μονάδας",
  "sources.projectSimHint": "Δημιουργεί μία παρακολουθούμενη μονάδα από το αποθηκευμένο έργο και την τελευταία αποθηκευμένη εκτίμηση παραγωγής ΦΒ. Παράγει ιστορικό λειτουργίας, πραγματική έναντι αναμενόμενης παραγωγής και ενεργά και ιστορικά σφάλματα. Όλα επισημαίνονται ως προσομοιωμένα.",
  "sources.projectSimDays": "Ιστορικό λειτουργίας (ημέρες)",
  "sources.projectSimDaysHint": "Επιλέξτε από 7 έως 365 ημέρες.",
  "sources.projectSimReplace": "Αντικατάσταση του γενικού χαρτοφυλακίου επίδειξης πέντε πάρκων",
  "sources.projectSimForecast": "Το έργο πρέπει να είναι αποθηκευμένο και η Εκτίμηση στην Παραγωγή ΦΒ να είναι ενημερωμένη.",
  "sources.projectSimRunning": "Δημιουργία προσομοίωσης έργου…",
  "sources.projectSimCreate": "Δημιουργία προσομοίωσης",
  "sources.projectSimDone": "Η μονάδα {{name}} είναι έτοιμη με {{days}} ημέρες προσομοιωμένου ιστορικού λειτουργίας.",
  "sources.projectSimFailed": "Δεν ήταν δυνατή η δημιουργία της προσομοίωσης έργου.",
  "sources.col.park": "Πάρκο",
  "sources.col.vendor": "Πηγή",
  "sources.col.capacity": "Ισχύς",
  "sources.col.interval": "Λήψη",
  "sources.col.lastPoll": "Τελευταία λήψη",
  "sources.col.lastReading": "Τελευταία μέτρηση",
  "sources.col.status": "Κατάσταση",
  "sources.pollNow": "Λήψη τώρα",
  "sources.test": "Έλεγχος σύνδεσης",
  "sources.edit": "Επεξεργασία",
  "sources.delete": "Διαγραφή",
  "sources.deleteConfirm": "Να διαγραφεί το πάρκο με όλες τις μετρήσεις και τους συναγερμούς του;",
  "sources.enabled": "Ενεργό",
  "sources.disabled": "Ανενεργό",
  "sources.poll.ok": "OK",
  "sources.poll.error": "Σφάλμα",
  "sources.poll.not_wired": "Αναμονή σύνδεσης",
  "sources.poll.never": "Δεν έχει γίνει λήψη",
  "sources.everyMin": "κάθε {{n}} λεπτά",
  "sources.empty": "Δεν υπάρχουν πάρκα ακόμη. Προσθέστε το πρώτο ή φορτώστε το χαρτοφυλάκιο επίδειξης.",
  "sources.wiredHint": "Ο σύνδεσμος για αυτό το portal έχει προετοιμαστεί αλλά δεν έχει ενεργοποιηθεί ακόμη: τα διαπιστευτήρια αποθηκεύονται και η λήψη ξεκινά μόλις ενεργοποιηθεί.",
  "sources.testOk": "Η σύνδεση λειτουργεί.",
  "sources.testFailed": "Η σύνδεση απέτυχε",
  "sources.polled": "Λήψη: {{n}} μετρήσεις, {{alarms}} ενεργές συνθήκες.",
  "csv.open": "Εισαγωγή CSV / Replay",
  "csv.title": "Εισαγωγή CSV παρακολούθησης",
  "csv.title.append": "Εισαγωγή επιπλέον δεδομένων CSV",
  "csv.intro": "Ελέγξτε ένα read-only export από portal ή SCADA και κατόπιν διατηρήστε τις αρχικές χρονικές σημάνσεις ως ιστορικό ή μετακινήστε ολόκληρη τη σειρά στο παρόν για ελεγχόμενο replay. Η PYTHIA αποθηκεύει κανονικοποιημένη τηλεμετρία και checksum· δεν διατηρεί το αρχικό αρχείο.",
  "csv.template": "Λήψη προτύπου",
  "csv.choose": "Επιλογή CSV",
  "csv.noFile": "Δεν επιλέχθηκε αρχείο",
  "csv.mode": "Τρόπος εισαγωγής",
  "csv.mode.replay": "Replay έως τώρα",
  "csv.mode.historical": "Διατήρηση ιστορικού χρόνου",
  "csv.mode.replayHint": "Μετακινούνται μόνο οι χρονικές σημάνσεις. Οι τιμές και τα διαστήματα μετρήσεων δεν αλλάζουν. Η τελευταία μέτρηση τοποθετείται στο τελευταίο ολοκληρωμένο διάστημα.",
  "csv.mode.historicalHint": "Οι αρχικές χρονικές σημάνσεις διατηρούνται. Η ιστορική εισαγωγή δεν δημιουργεί ενεργούς συναγερμούς του παρόντος.",
  "csv.validate": "Έλεγχος",
  "csv.import": "Εισαγωγή",
  "csv.valid": "Ο έλεγχος ολοκληρώθηκε επιτυχώς",
  "csv.summary": "{{readings}} μετρήσεις · {{devices}} συσκευές · {{inverters}} αντιστροφείς · {{events}} συμβάντα portal · ανάλυση {{sample_min}} λεπτών",
  "csv.imported": "Εισήχθησαν {{rows}} μετρήσεις από {{devices}} συσκευές.",
  "csv.append": "Εισαγωγή νέου CSV",
  "csv.badge": "CSV / replay",
  "csv.sampleEvery": "δείγματα ανά {{n}} λεπτά",

  "form.title.new": "Νέο πάρκο",
  "form.title.edit": "Επεξεργασία πάρκου",
  "form.section.identity": "Πάρκο",
  "form.section.plant": "Χαρακτηριστικά εγκατάστασης",
  "form.section.plantHint": "Όσα χρειάζονται το μοντέλο αναμενόμενης παραγωγής και οι κανόνες συναγερμών. Ισχύς και τοποθεσία είναι υποχρεωτικά· τα υπόλοιπα βελτιώνουν την αναμενόμενη καμπύλη.",
  "form.section.source": "Πηγή δεδομένων",
  "form.section.sourceHint": "Το portal στο οποίο αναφέρει σήμερα το πάρκο. Δεν αντικαθίσταται τίποτα στο πεδίο.",
  "form.name": "Όνομα πάρκου",
  "form.asset": "Μονάδα της πλατφόρμας (προαιρετικό)",
  "form.assetHint": "Σύνδεση με μονάδα του έργου, ώστε το πάρκο να εμφανίζεται και στις Λειτουργίες.",
  "form.assetNone": "Χωρίς σύνδεση",
  "form.dc": "Εγκατεστημένη ισχύς DC (kWp)",
  "form.ac": "Ισχύς AC (kW)",
  "form.lat": "Γεωγραφικό πλάτος",
  "form.lon": "Γεωγραφικό μήκος",
  "form.tz": "Ζώνη ώρας",
  "form.tilt": "Κλίση (°)",
  "form.azimuth": "Αζιμούθιο (°, 180 = νότος)",
  "form.mounting": "Στήριξη",
  "form.mounting.fixed": "Σταθερή κλίση",
  "form.mounting.tracker": "Ιχνηλάτης ενός άξονα",
  "form.commissioned": "Ημερομηνία ηλέκτρισης",
  "form.module": "Μοντέλο Φ/Β πλαισίου",
  "form.inverterModel": "Μοντέλο αντιστροφέα",
  "form.inverterCount": "Πλήθος αντιστροφέων",
  "form.prTarget": "Στόχος EPI (0-1)",
  "form.prTargetHint": "Ο λειτουργικός στόχος πραγματικής προς αναμενόμενη ενέργεια. Δεν είναι το συμβατικό PR που βασίζεται στην ακτινοβολία.",
  "form.loss": "Απώλειες συστήματος (%)",
  "form.lossHint": "Καλωδιώσεις, ρύπανση, αναντιστοιχία και διαθεσιμότητα μαζί. Προσαρμόστε μετά από μερικές εβδομάδες δεδομένων.",
  "form.vendor": "Portal / κατασκευαστής",
  "form.vendorSite": "Αναγνωριστικό πάρκου στο portal",
  "form.vendorSiteHint": "Το station / site / system id που χρησιμοποιεί το portal για αυτό το πάρκο. Αφήστε το κενό για να ληφθεί το πρώτο πάρκο που βλέπουν τα διαπιστευτήρια.",
  "form.interval": "Διάστημα λήψης (λεπτά)",
  "form.intervalHint": "Τουλάχιστον {{min}} λεπτά για αυτό το portal.",
  "form.enabled": "Λήψη ενεργή",
  "form.credentialsKept": "Τα αποθηκευμένα διαπιστευτήρια διατηρούνται αν δεν εισαγάγετε νέα.",
  "form.accessHowto": "Πώς παρέχει πρόσβαση ο πελάτης",
  "form.capabilities": "Παρέχει",
  "form.cap.inverterLevel": "επίπεδο αντιστροφέα",
  "form.cap.stringLevel": "επίπεδο στοιχειοσειράς",
  "form.cap.moduleLevel": "επίπεδο πλαισίου (με βελτιστοποιητές)",
  "form.cap.events": "συμβάντα σφαλμάτων",
  "form.cap.siteMeter": "μετρητή πάρκου",
  "form.costNote": "Κόστος",
  "form.save": "Αποθήκευση πάρκου",
  "form.cancel": "Ακύρωση",
  "form.required": "Υποχρεωτικό",
  "form.error": "Δεν ήταν δυνατή η αποθήκευση του πάρκου.",

  "common.kw": "kW",
  "common.kwp": "kWp",
  "common.kwh": "kWh",
  "common.mwh": "MWh",
  "common.na": "—",
  "common.of": "από",
  "common.min": "λεπτά",
  "common.error": "Κάτι πήγε στραβά.",
  "common.retry": "Επανάληψη",
};

export const MONITORING_TEXT = { en: EN, el: EL };

const interpolate = (value, vars = {}) =>
  String(value).replace(/\{\{(\w+)\}\}/g, (_, k) => (vars[k] === undefined || vars[k] === null ? "" : String(vars[k])));

export function monitoringText(language, key, vars) {
  const table = language === "el" ? EL : EN;
  return interpolate(table[key] ?? EN[key] ?? key, vars);
}

/** t(key, vars) in the current UI language. */
export function useMonitoringText() {
  const { language } = useLanguage();
  return useCallback((key, vars) => monitoringText(language, key, vars), [language]);
}

const pct = (v) => (v === null || v === undefined ? "" : Math.round(Number(v) * 100));

/** The alarm row's text, from (rule, payload) so it follows the UI language. */
export function alarmMessage(language, alarm) {
  const p = alarm?.payload || {};
  const device = p.device || alarm?.device || "";
  switch (alarm?.rule) {
    case "comm_loss":
      return p.minutes_silent == null
        ? monitoringText(language, "msg.comm_loss.never", { device })
        : monitoringText(language, "msg.comm_loss", { device, minutes: p.minutes_silent });
    case "inverter_offline":
      return monitoringText(language, "msg.inverter_offline", { device, siblings_pct: pct(p.siblings_share) });
    case "inverter_underperformance":
      return monitoringText(language, "msg.inverter_underperformance", { device, relative_pct: pct(p.relative) });
    case "plant_underperformance":
      return monitoringText(language, p.basis === "clearsky" ? "msg.plant_underperformance.clearsky" : "msg.plant_underperformance", { ratio_pct: pct(p.ratio) });
    case "string_deviation":
      return monitoringText(language, "msg.string_deviation", { device, mppts: (p.mppts || []).join(", ") });
    case "vendor_event":
      return monitoringText(language, "msg.vendor_event", { device: device ? `${device} · ` : "", code: p.code || "", vendor_message: p.vendor_message || "" });
    default:
      return alarm?.message || "";
  }
}

/** Rule description with its parameters filled in (percentages from ratios). */
export function ruleDescription(language, rule, params = {}) {
  const vars = { ...params };
  for (const k of ["ratio", "ratio_weather", "ratio_clearsky", "deviation", "siblings_min_share"]) {
    if (vars[k] != null) vars[`${k}_pct`] = pct(vars[k]);
  }
  return monitoringText(language, `rule.desc.${rule}`, vars);
}
